/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import LogoDark from '../../images/logo/gitme-logo-white.svg';
import { validateEmail } from '../../utils';
import UserAgreementModal from '../../components/Payment/UserAgreementModal';
import Loader from '../../common/Loader';
import Loading from '../../components/Loading';
import { INTEGRATION_RETURN_PATH_KEY } from '../../utils/integrations';

const GetStarted = () => {
  const location = useLocation();

  const [userInfo, setUserInfo] = useState(null);
  //  const [selectedRepoIds, setSelectedRepoIds] = useState([]);
  const [email, setEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [isMerged, setIsMerged] = useState(false);
  const showSuccess = false;
  const [password, setPassword] = useState('');

  const [hasAcceptedAgreement, setHasAcceptedAgreement] = useState(false);
  const [openAcceptedAgreement, setOpenAcceptedAgreement] = useState(false);

  const fetchWithToken = async ({
    url,
    method = 'GET',
    data = null,
    onSuccess,
    onError
  }) => {
    const token = new URLSearchParams(location.search).get('token');
    if (!token) return;

    try {
      const res = await axios({
        url,
        method,
        data,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      onSuccess?.(res.data || res);
    } catch (err) {
      console.error('Request failed:', err);
      onError?.(err);
    }
  };

  useEffect(() => {
    fetchWithToken({
      url: `${process.env.REACT_APP_BACKEND_URL}/github/get-data`,
      onSuccess: data => setUserInfo(data?.data),
      onError: () => toast.error('Not found user')
    });
  }, [location]);

  useEffect(() => {
    const existingAuthToken = localStorage.getItem('authToken');
    const githubToken = new URLSearchParams(location.search).get('token');

    if (!existingAuthToken || !githubToken) {
      return;
    }

    setLoading(true);

    axios
      .post(
        `${process.env.REACT_APP_BACKEND_URL}/github/lead-existing-user`,
        { githubToken },
        {
          headers: {
            Authorization: `Bearer ${existingAuthToken}`,
            'Content-Type': 'application/json'
          }
        }
      )
      .then(res => {
        if (res?.data?.message === 'SUCCESS') {
          const returnPath = localStorage.getItem(INTEGRATION_RETURN_PATH_KEY);
          if (returnPath) {
            localStorage.removeItem(INTEGRATION_RETURN_PATH_KEY);
            window.location.href = returnPath;
            return;
          }

          window.location.href = '/';
          return;
        }

        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to lead existing user', err);
        if (err?.response?.data?.error === 'SESSION_MISSING') {
          localStorage.removeItem('authToken');
        }
        setLoading(false);
      });
  }, [location.search]);

  const sendEmailCode = async (merge = false) => {
    if (!validateEmail(email)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    setIsMerged(false);
    fetchWithToken({
      url: `${process.env.REACT_APP_BACKEND_URL}/auth/send-code`,
      method: 'POST',
      data: { email: email, merge: merge },
      onSuccess: dataResult => {
        if (dataResult.existingMerge) {
          setIsMerged(true);
          toast.warning(
            'You have an existing company with this email. Merge this account with your existing company.'
          );
        }
        toast.success('Verification code sent to your email.');
        setIsCodeSent(true);
        setLoading(false);
      },
      onError: err => {
        toast.error(
          err.response?.data?.message || 'Failed to send verification code.'
        );
        setLoading(false);
      }
    });
  };

  const verifyEmailCode = async () => {
    const normalizedEmail = email.trim();
    const normalizedCode = emailCode.trim();

    if (!normalizedCode) {
      toast.error('Please enter the verification code.');
      return;
    }

    setLoading(true);
    try {
      fetchWithToken({
        url: `${process.env.REACT_APP_BACKEND_URL}/auth/verify-code`,
        method: 'POST',
        data: { email: normalizedEmail, code: normalizedCode },
        onSuccess: response => {
          if (response?.success !== true) {
            toast.error('Invalid or expired code.');
            return;
          }

          toast.success('Email verified successfully!');
          setIsEmailVerified(true);
        },
        onError: err => {
          toast.error(
            err.response?.data?.message || 'Invalid or expired code.'
          );
          setLoading(false);
        }
      });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid or expired code.');
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    setLoading(true);

    fetchWithToken({
      url: `${process.env.REACT_APP_BACKEND_URL}/github/lead-register`,
      method: 'POST',
      data: { companyName, fullName, email, password, isMerged },
      onSuccess: response => {
        const loginToken = response?.loginInfo?.token;

        const redirectToPanel = () => {
          if (loginToken) {
            localStorage.setItem('authToken', loginToken);
          }

          const panelUrl = new URL('https://panel.gitdotme.com/');
          if (loginToken) {
            panelUrl.searchParams.set('token', loginToken);
          }

          window.location.href = panelUrl.toString();
        };

        if (response?.message === 'SUCCESS' && response?.type === 'merged') {
          localStorage.setItem('authToken', '');
          toast.success(
            'Account merged successfully! Redirecting to login page.'
          );
          setTimeout(() => {
            window.location.href = '/';
          }, 2000);
          return;
        }

        if (response?.message === 'SUCCESS' && response?.type === 'updated') {
          toast.success('Account updated successfully! Redirecting to panel.');
          redirectToPanel();
          return;
        }

        if (response?.message === 'SUCCESS' && loginToken) {
          localStorage.setItem('authToken', loginToken);
          window.location.href = '/settings/integrations?integration=connected';
          return;
        }
        setLoading(false);
      },
      onError: err => {
        setLoading(false);
        console.error('Failed to analyze repos', err);
        toast.error('Error: ' + err?.response?.data?.message);
      }
    });
  };

  const handleStarted = async () => {
    if (!hasAcceptedAgreement) {
      toast.error('Please accept the agreement to proceed.');
      return;
    }

    if (!companyName) {
      toast.error('Please enter your company name.');
      return;
    }

    if (!fullName) {
      toast.error('Please enter your full name.');
      return;
    }

    if (!isEmailVerified) {
      toast.error('Please verify your email before proceeding.');
      return;
    }

    if (!isMerged && (!password.length || password.length < 3)) {
      toast.error('Please enter your password .');
      return;
    }

    handleStart();
  };

  if (!userInfo) return null;

  return (
    <>
      {loading && <Loader />}
      <div className="min-h-screen flex bg-gray-100">
        <div className="w-full md:w-1/2 flex items-center justify-center p-10 bg-white">
          {showSuccess ? (
            <div className="left-6 z-50 rounded-xl px-6 py-4 text-blue-800 w-full">
              <div className="flex items-start space-x-3">
                <div className="text-2xl">✅</div>
                <div className="flex flex-col gap-5">
                  <span className="font-semibold text-2xl">
                    Successfully submitted!
                  </span>
                  <span className="text-xl mt-1">
                    Your contact information has been received. We’ll be in
                    touch shortly.
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-md w-full">
              <h2 className="text-3xl font-bold mb-4 text-black">
                Let{"'"}s get started
              </h2>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Username: {userInfo?.info?.username}
                </label>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={e => setCompanyName(e.target.value)}
                  placeholder="Enter your company"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  placeholder="Enter your full name"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none"
                />
              </div>

              {/* Email + Doğrulama */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Email</label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={e => {
                      setEmail(e.target.value);
                      setIsEmailVerified(false);
                      setIsCodeSent(false);
                      setEmailCode('');
                    }}
                    placeholder="Enter your email"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none"
                    disabled={isEmailVerified}
                  />

                  {!isCodeSent && !isEmailVerified && (
                    <button
                      onClick={() => sendEmailCode(false)}
                      disabled={loading}
                      className="bg-blue-600 text-white px-3 py-2 rounded-md text-sm hover:bg-blue-700 transition"
                    >
                      {loading ? <Loading type="inline" /> : 'Send Code'}
                    </button>
                  )}
                </div>

                {isCodeSent && !isEmailVerified && (
                  <div className="mt-3 flex gap-2">
                    <input
                      type="text"
                      value={emailCode}
                      onChange={e => setEmailCode(e.target.value)}
                      placeholder="Enter verification code"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none"
                    />
                    <button
                      onClick={verifyEmailCode}
                      disabled={loading}
                      className="bg-green-600 text-white px-3 py-2 rounded-md text-sm hover:bg-green-700 transition"
                    >
                      {loading ? <Loading type="inline" /> : 'Verify'}
                    </button>
                  </div>
                )}

                {isEmailVerified && !isMerged && (
                  <>
                    <p className="text-green-600 text-sm mt-1">
                      ✅ Email verified
                    </p>

                    <div className="mt-3">
                      <label className="block text-sm font-medium mb-1">
                        Password
                      </label>
                      <input
                        type="password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none"
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="pt-4">
                <label className="flex items-start text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="mt-1 mr-2 accent-yellow-500"
                    onChange={e => {
                      setHasAcceptedAgreement(e.target.checked);
                      setOpenAcceptedAgreement(e.target.checked);
                    }}
                    checked={hasAcceptedAgreement}
                  />
                  <span>I have read and accept the agreement.</span>
                </label>
              </div>

              <UserAgreementModal
                isOpen={openAcceptedAgreement}
                onClose={() => setOpenAcceptedAgreement(false)}
              />

              <button
                className="mt-6 w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 transition"
                onClick={handleStarted}
                disabled={loading}
              >
                {loading ? <Loading type="inline" /> : 'Get Started'}
              </button>
            </div>
          )}
        </div>

        <div className="hidden md:flex w-1/2 bg-blue-600 items-center justify-center p-10 rounded-l-[2rem] flex-col text-center text-white">
          <img
            className="dark:block mb-6"
            src={LogoDark}
            alt="Logo"
            style={{ width: '10rem' }}
          />
          <p className="text-lg font-semibold mb-2 max-w-md">
            A New Standard in Developer Performance Analytics.
          </p>
          <p className="text-sm font-light max-w-md leading-relaxed">
            Gitme leverages advanced AI to deliver accurate, unified, and
            transparent insights — replacing fragmented metrics with a single
            source of truth.
          </p>
        </div>
      </div>
    </>
  );
};

export default GetStarted;
